/**
 * PublicCampaignShell — what an ANONYMOUS visitor sees behind a public,
 * view-only share link.
 *
 * The point of the feature is that this is NOT a stripped-down microsite: it is
 * the real campaign-detail chrome (main rail → campaign nav → top bar →
 * content), with exactly ONE live surface — the screen the link granted — and
 * every other surface masked. So the shell mounts the SAME `AppSidebar` /
 * `CampaignNavSidebar` / review panels / tab components the authenticated app
 * uses, fed from the already-fetched `PublicShareView` payload. This is
 * literally true for every page-kind roster tab too (Tasks 16-20): a
 * `creators`/`confirmed`/`scripts`/`content`/`live-content` grant mounts the
 * REAL `CreatorsTab`/`ConfirmedTab`/`ScriptsTab`/`ContentTab`/`LiveContentTab`
 * component, in a read-only public mode with permanently-skeletoned
 * unavailable data — not a simplified stand-in.
 *
 * What it deliberately does NOT do:
 *  - mount `BrandLayout` / `AppLayout` (both reach for `useAuth`, `useProfile`
 *    and the notification bell — all authed). The top bar here is a static
 *    stand-in with the same 52px/off-white/sticky shape.
 *  - fetch anything. Every child is prop-fed; see publicShellViews.tsx.
 *  - own the login dialog. `onRequestLogin` is called and the PAGE renders the
 *    dialog as a React SIBLING of this shell — a child would have its own
 *    clicks swallowed by the interceptor below.
 *
 * Click interception (2026-08-05 redesign of spec §5.6's original deny-all):
 * a single React `onClickCapture` on the shell root, now TWO-TIER rather than
 * allow-vs-prompt:
 *
 *   - allowed (`[data-public-allowed-subtree]` or `[data-public-allowed]` —
 *     see `isAllowedClick`'s doc comment for the difference) — the click
 *     proceeds to whatever its own handler does. This is deliberately
 *     generous for the ENTIRE one granted view (ScriptReviewPanel/
 *     ContentReviewPanel's dialog root, SheetContent for insights/safety —
 *     see PublicGrantedOverlay's callees): every internal tab switch,
 *     accordion, GEO toggle etc. inside the ONE screen a link actually
 *     grants is real content, per the 2026-08-05 "share exact same content
 *     on public link as we show in private link" decision, and is safe to
 *     let run — every mutating control on that panel is either not rendered
 *     at all (canReview={false}, no onApprove, no onAddComment — see
 *     publicShellViews.tsx) or itself still wired to onRequestLogin as its
 *     OWN handler (onClose, cross-purpose tab switches). The narrower
 *     `data-public-allowed` additionally covers the two sidebars' collapse
 *     affordances (pure layout) and the "Sign in" button (whose own onClick
 *     IS the login prompt — see PublicTopBar).
 *   - anything else — cancelled SILENTLY. No dialog, no navigation, no
 *     handler. This is the fix for "clicking anywhere with no action button
 *     popped the sign-in dialog": a roster table's rows/cells (opening
 *     another creator's detail is reaching beyond what THIS link granted —
 *     "unclickable" is the product decision, not a gap) and the top bar
 *     breadcrumb are real, rendered, clickable-looking DOM — but not this
 *     link's content, so a click there does nothing rather than interrupting
 *     reading with a popup. The main-rail/campaign-nav items and status
 *     pills are the ONE deliberate exception: their own `onTabChange` calls
 *     `onRequestLogin` with a resumable `navigate`/`select_pill` pending
 *     action (see `handleNavTabChange`, `CreatorsTab.handlePublicPillClick`)
 *     — clicking the ALREADY-granted tab/pill still stays a no-op, but
 *     clicking a different one now prompts sign-in with a "continue here
 *     after signing in" offer, not silence.
 *
 * Only a control whose OWN onClick already calls `onRequestLogin` (Sign in,
 * the granted panel's close button, its scrim) still shows the dialog — the
 * capture handler no longer decides that on their behalf. A genuine future
 * write control that's accidentally left reachable fails safe: silently
 * inert, never a working mutation (permissions are already nulled here, and
 * the public endpoints reject writes server-side regardless).
 *
 * Must be React capture, not a native `addEventListener` on the root node —
 * several granted views portal themselves to `document.body` (ScriptReviewPanel
 * via createPortal, the insights/content drawers via Radix Sheet), so their
 * DOM subtree is NOT under the root and a native listener would never see
 * those clicks. React's synthetic capture phase follows the REACT tree, which
 * portals stay part of — HOWEVER `isAllowedClick`'s `Element.closest()` is a
 * real DOM-tree walk, so an allow marker only works on a portaled click when
 * it sits on a node that ends up INSIDE the portaled DOM subtree itself (see
 * ScriptReviewPanel.tsx/ContentReviewPanel.tsx's own dialog-root markers,
 * and SheetContent's `data-public-allowed-subtree` prop in
 * publicShellViews.tsx) — a wrapper in THIS file's JSX around
 * `<ScriptReviewPanel/>` would live in a completely different part of the
 * real DOM and never be found.
 */
import * as React from 'react';
import type { PublicShareView } from '@discovr/features';
import type { PendingPublicAction } from '@discovr/common';

import { AppSidebar, type NavItem } from '../components/common/AppSidebar';
import { CampaignNavSidebar } from '../components/common/CampaignNavSidebar';
import { useSidebarCollapse } from '../components/common/useSidebarCollapse';
import { NarrowOverlayBackdrop, useNarrowOverlayDismiss } from '../components/common/narrowOverlay';
import { House, PaperPlaneTilt, Plug, Gear, Question, CaretRight } from '../design-system/icons';
import logoWordmark from '../assets/logo-wordmark.svg';
import logoMark from '../assets/logo-icon-black.svg';
import { buildNavItems } from './utils/navBuilder';
import { buildBrandPermissions } from '../lib/campaignPermissions';
import { usePublicView } from './publicView';
import { CreatorsTab } from './components/CreatorsTab';
import { ConfirmedTab } from './components/ConfirmedTab';
import { ScriptsTab } from './components/ScriptsTab';
import { ContentTab } from './components/ContentTab';
import { LiveContentTab } from './components/LiveContentTab';
import { CampaignDetailsTab } from './components/CampaignDetailsTab';
import { PublicShopifyCouponsTab } from './components/PublicShopifyCouponsTab';
import {
  MaskedPanel,
  PublicContentPanel,
  PublicConversationPanel,
  PublicInsightsPanel,
  PublicReportingCard,
  PublicSafetyPanel,
  PublicScriptPanel,
} from './publicShellViews';

// ─── Grant → tab mapping ─────────────────────────────────────────────────────

/**
 * Creator-kind grants name a PURPOSE (the panel), not a tab; the tab is
 * whichever roster screen that panel opens over in the authenticated app.
 * Mirrors PANEL_BY_PURPOSE in share/shortLink.ts.
 */
/** Lowest possible permission set — `buildNavItems` takes one, and a public
 *  visitor is strictly read-only. */
const PUBLIC_PERMISSIONS = buildBrandPermissions('brand_member', 'read');

const TAB_BY_PURPOSE: Record<string, string> = {
  script: 'scripts',
  content: 'content',
  insights: 'creators',
  safety: 'creators',
  chat: 'creators',
};

/** The single tab this link pins the shell to. Never changes — see the nav's
 *  `onTabChange` below. */
export function grantedTab(grant: PublicShareView['grant']): string {
  if (grant.kind === 'creator') return TAB_BY_PURPOSE[grant.purpose ?? ''] ?? 'creators';
  return grant.tab || 'details';
}

// A genuinely inert callback for chrome that isn't this link's granted
// content (nav items, the masked main rail) — clicking does nothing at all,
// not even a re-render. One shared reference so it never shows up as a prop
// diff either.
function PUBLIC_NOOP() {
  /* deliberately empty — see this file's header */
}

// ─── Click allow-list ────────────────────────────────────────────────────────

/** Anything that can act on a click — the set `handleRailExpand` already
 *  ignores in both sidebars, kept in sync deliberately. */
const INTERACTIVE_SELECTOR = 'button, a, input, select, textarea, [role="button"]';

/**
 * Is this click one of the few the public shell lets through? Two distinct
 * markers, two distinct rules — do not conflate them:
 *
 * `[data-public-allowed-subtree]` — EVERYTHING inside is allowed, including
 * nested interactive controls. Used exactly once in spirit: the one granted
 * view (ScriptReviewPanel/ContentReviewPanel's dialog root, SheetContent for
 * the insights/safety panels) — see PublicGrantedOverlay's callees. A single
 * ancestor check; no container-vs-control distinction needed because this
 * marker's whole point is "stop distinguishing, this subtree IS the content".
 *
 * `data-public-allowed` — the ORIGINAL, narrower marker (unchanged since
 * before the 2026-08-05 interaction-model rework) — marks two different
 * kinds of node, and they need different rules:
 *
 *  - a CONTROL that carries it (each sidebar's collapse toggle, the "Sign
 *    in" button) — always allowed; each is a single, self-contained
 *    affordance with no interactive descendants to worry about leaking.
 *  - a CONTAINER that carries it (a COLLAPSED rail, whose bare-body click
 *    expands it) — allowed only for clicks that aren't on some OTHER
 *    interactive control inside it. Without this distinction, collapsing a
 *    rail would put every masked nav item inside an allowed subtree and
 *    quietly disarm interception for the whole nav — exactly the leak
 *    `-subtree` above deliberately accepts ONLY for the one granted view.
 *
 * So: subtree marker anywhere in the ancestry short-circuits to allowed.
 * Otherwise, find the nearest interactive ancestor. If there isn't one, the
 * click is bare container/chrome — allowed iff it's inside an allowed scope.
 * If there is one, it must carry the (non-subtree) attribute ITSELF.
 */
function isAllowedClick(target: HTMLElement): boolean {
  if (target.closest('[data-public-allowed-subtree]')) return true;

  const allowedScope = target.closest('[data-public-allowed]');
  if (!allowedScope) return false;

  const control = target.closest(INTERACTIVE_SELECTOR);
  if (!control) return true;
  // `closest` from the control itself returns the control when it carries the
  // attribute, and an ancestor container otherwise.
  return control.closest('[data-public-allowed]') === control;
}

// ─── Props ───────────────────────────────────────────────────────────────────

export interface PublicCampaignShellProps {
  /** The whole public payload — campaign context, grant descriptor, and at
   *  most one view body. */
  data: PublicShareView;
  /**
   * Open the page-owned login dialog. Called for every intercepted click and
   * from every masked affordance. Accepts an optional `PendingPublicAction`
   * so a genuine write control on the one granted view (e.g. CommercialPopover's
   * Save) can capture the visitor's in-progress intent for resume-after-sign-in
   * — every other call site keeps calling this bare.
   */
  onRequestLogin: (action?: PendingPublicAction) => void;
}

// ─── Shell ───────────────────────────────────────────────────────────────────

export function PublicCampaignShell({
  data,
  onRequestLogin,
}: PublicCampaignShellProps): React.ReactElement {
  const { campaign, grant } = data;
  const activeTab = grantedTab(grant);
  // Sourced from context (same as `PublicGrantedOverlay` below) rather than a
  // prop — this component already renders inside `PublicShareViewPage`'s
  // `PublicViewProvider`. Needed to stamp a `navigate` pending action with
  // the link it came from, same as `CommercialPopover`'s `save_deliverables`
  // action does for campCode/tail.
  const { campCode: publicCampCode, tail: publicTail } = usePublicView();

  // Main rail: starts collapsed and is NOT persisted, mirroring AppLayout's
  // `collapseRail` on campaign routes (an anonymous visit must not write the
  // signed-in user's rail preference into their localStorage either).
  const [mainCollapsed, setMainCollapsed] = React.useState(true);
  const toggleMainCollapsed = React.useCallback(() => setMainCollapsed((v) => !v), []);
  const {
    dismiss: dismissMainRail,
    toggleRef: mainRailToggleRef,
    trapRef: mainRailTrapRef,
  } = useNarrowOverlayDismiss(
    !mainCollapsed,
    toggleMainCollapsed,
  );
  // Campaign nav: same key CampaignDetailPage uses, so the rail width the
  // visitor picks carries over if they later sign in on this browser.
  const { collapsed: navCollapsed, toggleCollapsed: toggleNavCollapsed } = useSidebarCollapse(
    'discovr:campaign-nav-collapsed',
    { collapseOnNarrow: true },
  );

  const navItems = React.useMemo(
    () =>
      buildNavItems({
        permissions: PUBLIC_PERMISSIONS,
        // No counts: a public link shares one screen, never the campaign's
        // pending-work totals.
        counts: {},
        // Shopify is a per-campaign integration the public payload doesn't
        // report; surface the item only when the link itself grants it, so the
        // active nav entry always exists.
        shopifyEnabled: activeTab === 'shopify',
        platform: campaign.platform,
      }),
    [activeTab, campaign.platform],
  );

  const tabLabel = navItems.find((i) => i.id === activeTab)?.label ?? 'Details';

  // Clicking a nav item this link doesn't grant used to be a genuine no-op
  // (see `PUBLIC_NOOP` above) — now it captures the visitor's intent and
  // prompts sign-in, mirroring every other genuine action control on this
  // surface (CommercialPopover's Save, the granted panel's own controls).
  // Clicking the ALREADY-active (granted) tab is left as a no-op still: the
  // visitor is already looking at it, so there's nothing to resume and a
  // sign-in prompt there would just be noise.
  const handleNavTabChange = React.useCallback(
    (tab: string) => {
      if (tab === activeTab) return;
      if (publicCampCode && publicTail) {
        onRequestLogin({
          type: 'navigate',
          campCode: publicCampCode,
          tail: publicTail,
          tab,
          createdAt: Date.now(),
        });
      } else {
        // Missing identifying context (shouldn't happen on a real share
        // link — defensive only, mirrors CommercialPopover's handleSave).
        onRequestLogin();
      }
    },
    [activeTab, onRequestLogin, publicCampCode, publicTail],
  );

  const handleClickCapture = React.useCallback((e: React.MouseEvent) => {
    const target = e.target as HTMLElement | null;
    if (target && isAllowedClick(target)) return;
    // Cancel SILENTLY: no navigation, no panel handler, no dialog. A control
    // whose own onClick already calls onRequestLogin (Sign in, the granted
    // panel's close/scrim) still shows it — that's the control's own doing,
    // not this handler's — see the file header.
    e.preventDefault();
    e.stopPropagation();
  }, []);

  return (
    <div
      data-testid="public-campaign-shell"
      className="relative min-h-screen font-sans"
      onClickCapture={handleClickCapture}
    >
      {!mainCollapsed && <NarrowOverlayBackdrop onDismiss={dismissMainRail} />}
      <AppSidebar
        {...maskedAppSidebarProps()}
        collapsed={mainCollapsed}
        onToggleCollapse={toggleMainCollapsed}
        expandToggleRef={mainRailToggleRef}
        overlayRef={mainRailTrapRef}
      />

      <main
        className={[
          'box-border flex min-h-screen flex-1 flex-col bg-background transition-[margin] duration-[260ms] ease-[cubic-bezier(0.32,0.72,0,1)] will-change-[margin]',
          mainCollapsed ? 'ml-[64px]' : 'ml-[64px] md:ml-56',
        ].join(' ')}
      >
        <PublicTopBar
          campaignName={campaign.name}
          tabLabel={tabLabel}
          onRequestLogin={onRequestLogin}
        />

        <div className="flex w-full min-w-0 flex-1">
          <CampaignNavSidebar
            items={navItems}
            activeTab={activeTab}
            // Clicking a nav item this link doesn't grant now prompts
            // sign-in (with the click captured as a resumable `navigate`
            // pending action) instead of the old silent no-op — see
            // `handleNavTabChange` above. `activeTab` still stays pinned to
            // the granted tab for the whole (unauthenticated) session
            // either way; only a real sign-in can actually change it.
            onTabChange={handleNavTabChange}
            onBack={PUBLIC_NOOP}
            backLabel="campaigns"
            collapsed={navCollapsed}
            onToggleCollapse={toggleNavCollapsed}
          />

          <div className="flex min-w-0 flex-1 flex-col items-center">
            <div className="box-border flex w-full max-w-[1200px] flex-1 flex-col gap-5 p-8">
              {/* Visually the campaign is named once, in the top bar. This
                  heading stays for assistive tech only: the top bar states
                  the name inside a Breadcrumb nav, so dropping the visible
                  header block would otherwise leave the page with no <h1> at
                  all. The platform + short-code meta line is gone entirely —
                  that was the duplicated identity on every tab's table.
                  Skipped for the five page-kind roster grants — their real
                  tab component (CreatorsTab.tsx etc.) already renders its own
                  visible <h1> via RosterScreenHeader, and it isn't masked
                  here, so adding a second <h1> would give the document two
                  top-level headings instead of filling a real gap. */}
              {!tabHasOwnHeading(data) && <h1 className="sr-only">{campaign.name}</h1>}
              <PublicTabBody data={data} tabLabel={tabLabel} />
            </div>
          </div>
        </div>
      </main>

      <PublicGrantedOverlay data={data} onRequestLogin={onRequestLogin} />
    </div>
  );
}

// ─── Chrome ──────────────────────────────────────────────────────────────────

/**
 * Static stand-in for `AppTopBar`. Same shape (52px, off-white, sticky,
 * bottom border) minus the notification bell, which is an authed query. The
 * breadcrumb itself is inert chrome (silently swallowed by click-capture,
 * same as the nav) — "Sign in" is the one deliberate CTA, so it carries
 * `data-public-allowed` + a real `onClick`: under the new two-tier model,
 * nothing shows the login dialog unless its OWN handler says so.
 */
function PublicTopBar({
  campaignName,
  tabLabel,
  onRequestLogin,
}: {
  campaignName: string;
  tabLabel: string;
  onRequestLogin: (action?: PendingPublicAction) => void;
}) {
  // Rendered inside PublicViewProvider (see PublicCampaignShell below), so
  // this reads the mismatch flag straight from context instead of needing
  // it threaded through as another prop — same reasoning as
  // PublicViewValue.isViewerAccountMismatch's own doc comment.
  const { isViewerAccountMismatch } = usePublicView();
  return (
    <header className="sticky top-0 z-30 h-13 shrink-0 border-b border-border bg-white">
      <div className="flex size-full items-center justify-between px-4">
        <nav aria-label="Breadcrumb" className="flex min-w-0 items-center gap-2 text-body-sm-medium">
          <span className="inline-flex items-center gap-1.5 text-muted-foreground">
            <House size={16} /> Home
          </span>
          <CaretRight size={14} className="shrink-0 text-muted-foreground/60" />
          <span className="truncate text-foreground">{campaignName}</span>
          <CaretRight size={14} className="shrink-0 text-muted-foreground/60" />
          <span className="shrink-0 text-foreground">{tabLabel}</span>
        </nav>
        <button
          type="button"
          data-public-allowed=""
          // Not a direct `onClick={onRequestLogin}` pass-through: onClick would
          // hand the DOM MouseEvent through as `onRequestLogin`'s (now widened)
          // first argument, which expects a PendingPublicAction, not an event.
          onClick={() => onRequestLogin()}
          className="flex h-8 items-center rounded-lg bg-foreground px-3.5 text-body-sm-medium text-white transition-colors hover:bg-foreground/90"
        >
          {isViewerAccountMismatch ? 'Switch account' : 'Sign in'}
        </button>
      </div>
    </header>
  );
}

// ─── Body / overlay dispatch ─────────────────────────────────────────────────

/**
 * The granted tab's BODY. Page-kind grants (roster, reporting, details,
 * shopify) are the body itself; creator-kind grants open a panel OVER the
 * roster screen, so their body is the masked decoy and the live surface
 * renders in `PublicGrantedOverlay`.
 *
 * Every page-kind tab the BE can grant a real payload for — the five roster
 * tabs, Reporting, and Details — mounts its REAL tab component:
 * pixel-identical to the authed app, in a read-only public mode driven
 * entirely by `usePublicView()` (see CreatorsTab.tsx / ConfirmedTab.tsx /
 * ScriptsTab.tsx / ContentTab.tsx / LiveContentTab.tsx /
 * CampaignDetailsTab.tsx and publicView.tsx). Shopify's Coupons sub-view is
 * the one exception: rather than a public-mode branch threaded through the
 * 1000+-line, mutation-heavy `ShopifySettingsTab`, it gets its own small,
 * separate, mutation-free component (`PublicShopifyCouponsTab.tsx`) — see
 * that file's doc comment for why. The BE's own `shareLinkTabs` grammar
 * (share_link_handler.go) never emits a roster grant outside the five
 * roster tabs, so the `MaskedPanel` fallback below is unreachable in
 * production today for THAT branch — it exists purely as a defensive guard
 * against a future/malformed tab value. Details/Shopify each have their own
 * explicit `data.details`/`data.shopify` checks instead of living inside
 * the roster branch.
 */
/** The five page-kind roster tabs, each mapped to the real component that
 *  renders it — and, via its own visible `<h1>` (RosterScreenHeader), the
 *  ONE thing this map's key set (ROSTER_TABS_WITH_OWN_H1 below) also drives:
 *  skipping the shell's sr-only `<h1>` for exactly these grants, so the
 *  document never ends up with two top-level headings. Single source of
 *  truth — a tab added/removed here can't drift out of sync with the
 *  heading-skip set the way two independently-maintained lists could. */
const ROSTER_TAB_COMPONENTS: Record<string, () => React.JSX.Element> = {
  creators: () => <CreatorsTab />,
  confirmed: () => <ConfirmedTab />,
  scripts: () => <ScriptsTab />,
  content: () => <ContentTab />,
  'live-content': () => <LiveContentTab />,
};
const ROSTER_TABS_WITH_OWN_H1 = new Set(Object.keys(ROSTER_TAB_COMPONENTS));

function tabHasOwnHeading(data: PublicShareView): boolean {
  return !!data.roster && ROSTER_TABS_WITH_OWN_H1.has(grantedTab(data.grant));
}

function PublicTabBody({ data, tabLabel }: { data: PublicShareView; tabLabel: string }) {
  if (data.roster) {
    const renderTab = ROSTER_TAB_COMPONENTS[grantedTab(data.grant)];
    if (renderTab) return renderTab();
    return <MaskedPanel label={tabLabel} />;
  }
  // grant.window (Task 12) is only ever set for a general-insights/sentiment
  // grant — PublicReportingCard renders it as a static label, or nothing at
  // all when absent (every other reporting virtual slug).
  if (data.reporting) return <PublicReportingCard reporting={data.reporting} window={data.grant.window} />;
  if (data.details) return <CampaignDetailsTab />;
  if (data.shopify) return <PublicShopifyCouponsTab />;
  return <MaskedPanel label={tabLabel} />;
}

/** The one live creator-kind panel, if this link granted one. */
function PublicGrantedOverlay({
  data,
  onRequestLogin,
}: {
  data: PublicShareView;
  onRequestLogin: (action?: PendingPublicAction) => void;
}) {
  // Sourced from usePublicView() (same pattern CreatorsTab.tsx uses for
  // CommercialPopover) rather than threaded as a prop from PublicCampaignShell
  // — this component already sits inside PublicShareViewPage's
  // PublicViewProvider. Both creator-kind grants that carry a resumable
  // Approve (script, content) need these — see PublicScriptPanel's /
  // PublicContentPanel's doc comments.
  const { campCode, tail } = usePublicView();
  if (data.script)
    return (
      <PublicScriptPanel script={data.script} campCode={campCode} tail={tail} onRequestLogin={onRequestLogin} />
    );
  if (data.content)
    return (
      <PublicContentPanel content={data.content} campCode={campCode} tail={tail} onRequestLogin={onRequestLogin} />
    );
  if (data.insights)
    return <PublicInsightsPanel insights={data.insights} onRequestLogin={onRequestLogin} />;
  if (data.safety) return <PublicSafetyPanel safety={data.safety} onRequestLogin={onRequestLogin} />;
  if (data.chat)
    return (
      <PublicConversationPanel chat={data.chat} campCode={campCode} tail={tail} onRequestLogin={onRequestLogin} />
    );
  return null;
}

// ─── Masked main rail ────────────────────────────────────────────────────────

const MASKED_MAIN_NAV: NavItem[] = [
  { id: 'overview', label: 'Home', icon: House },
  { id: 'campaigns', label: 'Campaign', icon: PaperPlaneTilt },
  { id: 'integration', label: 'Integration', icon: Plug },
];

const MASKED_FOOTER_NAV: NavItem[] = [
  { id: 'settings', label: 'Settings', icon: Gear },
  { id: 'help', label: 'Help Centre', icon: Question },
];

/**
 * The main rail exactly as the brand app renders it (same items, same order)
 * but with EVERY callback a genuine no-op — belt-and-braces behind the
 * click-capture handler, so even a click path that somehow escaped
 * interception still does nothing (2026-08-05: nothing here is this link's
 * granted content, so "does nothing" replaces the old "opens the login
 * dialog" — see the file header). Note the rail carries no `href` items
 * here: a router `Link` would navigate on middle-click/⌘-click, which no
 * click handler can cancel.
 */
function maskedAppSidebarProps() {
  return {
    navItems: MASKED_MAIN_NAV,
    footerItems: MASKED_FOOTER_NAV,
    activeTab: 'campaigns',
    onTabChange: PUBLIC_NOOP,
    onNavigateHome: PUBLIC_NOOP,
    onNavigateProfile: PUBLIC_NOOP,
    onSignOut: PUBLIC_NOOP,
    userInitials: '',
    userName: 'Guest',
    userRole: 'View-only link',
    logoSrc: logoWordmark,
    logoMarkSrc: logoMark,
    appName: 'Discovr',
  };
}

export default PublicCampaignShell;
